import { TrashIcon } from '@radix-ui/react-icons'
import { useRuntimeVariables } from 'app-kit'
import { TooltipWrapper } from 'components/TooltipWrapper'
import { useGeckoAnalytics } from 'features/analytics'
import { useGeckoAuth } from 'features/auth'
import { useKestrel } from 'features/kestrelViewer'
import { ViewToggle } from 'features/kestrelViewer/ui/tools/ViewToggle'
import React, { useState } from 'react'
import styled, { keyframes } from 'styled-components'

const fadeInModal = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95) translateY(-0.5rem);
  }
  to {
    opacity: 0.95;
    transform: scale(1) translateY(0);
  }
`

const ConfirmationBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`

const ConfirmationContainer = styled.div`
  border-radius: 0.5rem;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 1rem;
  color: white;
  line-height: 1.25rem;
  text-align: center;
  width: 20rem;
  display: flex;
  flex-direction: column;
  position: relative;
  opacity: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  animation: ${fadeInModal} 0.3s ease-out forwards;
`

const ConfirmationTitle = styled.h3`
  margin: 0 0 0.5rem 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: white;
  letter-spacing: 0.025rem;
  opacity: 0.95;
`

const ConfirmationMessage = styled.p`
  margin: 0 0 1rem 0;
  font-size: 0.875rem;
  opacity: 0.9;
  line-height: 1.4;
`

const ButtonContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: center;
`

const ConfirmButton = styled.button`
  background: rgba(220, 38, 38, 0.8) !important;
  color: white !important;
  border: 1px solid rgba(220, 38, 38, 0.6) !important;
  padding: 0.5rem 1rem !important;
  border-radius: 0.25rem !important;
  font-size: 0.875rem !important;
  cursor: pointer !important;
  flex: 1 !important;
  transition: all 0.2s ease !important;
  font-weight: 500 !important;
  display: inline-block !important;
  text-decoration: none !important;
  outline: none !important;
  box-sizing: border-box !important;

  &:hover {
    background: rgba(220, 38, 38, 0.9) !important;
    border-color: rgba(220, 38, 38, 0.8) !important;
  }

  &:active {
    background: rgba(220, 38, 38, 1) !important;
    transform: translateY(1px) !important;
  }
`

const CancelButton = styled.button`
  background: rgba(255, 255, 255, 0.15) !important;
  color: white !important;
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
  padding: 0.5rem 1rem !important;
  border-radius: 0.25rem !important;
  font-size: 0.875rem !important;
  cursor: pointer !important;
  flex: 1 !important;
  transition: all 0.2s ease !important;
  font-weight: 500 !important;
  display: inline-block !important;
  text-decoration: none !important;
  outline: none !important;
  box-sizing: border-box !important;

  &:hover {
    background: rgba(255, 255, 255, 0.25) !important;
    border-color: rgba(255, 255, 255, 0.4) !important;
  }

  &:active {
    background: rgba(255, 255, 255, 0.35) !important;
    transform: translateY(1px) !important;
  }
`

export const DeleteVMButton: React.FC = () => {
  const { kestrelModel, setPixelStreamingURL, setKestrelStatus } = useKestrel()
  const { authorization } = useGeckoAuth()
  const { trackButtonClicked } = useGeckoAnalytics()
  const { KESTREL_MATCHMAKER_URL } = useRuntimeVariables()
  const deleteURL = `${KESTREL_MATCHMAKER_URL}/api/v1/kestrel/delete-vm-by-model-id`
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showConfirmation, setShowConfirmation] = useState(false)

  const deleteVM = async () => {
    if (isLoading) return // Prevent multiple clicks while loading
    setIsLoading(true)
    setError(null)

    try {
      const response = await fetch(deleteURL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `${authorization}`,
        },
        body: JSON.stringify({
          kestrelModelId: kestrelModel?.data.kestrel_model_id,
        }),
      })

      if (!response.ok) {
        throw new Error(`Failed to delete VM: ${response.statusText}`)
      }

      // Reset Kestrel state and clear pixel streaming URL after successful deletion
      setPixelStreamingURL('')
      setKestrelStatus((prev) => ({
        ...prev,
        toggledOn: false,
        shouldConnect: false,
        videoReady: false,
      }))
    } catch (error) {
      console.error('Error connecting to matchmaker:', error)
      setError(
        error instanceof Error ? error.message : 'An unknown error occurred'
      )
    } finally {
      setIsLoading(false)
    }
  }

  const handleConfirm = () => {
    setShowConfirmation(false)
    deleteVM()
    trackButtonClicked({
      button_name: 'kestrel_delete_vm',
      kestrel_model_id: kestrelModel?.data.kestrel_model_id,
    })
  }

  const handleCancel = () => {
    setShowConfirmation(false)
  }

  const getTooltipLabel = () => {
    if (error) return `Error: ${error}`
    if (isLoading) return 'Deleting VM...'
    return 'Delete VM'
  }

  return (
    <>
      <TooltipWrapper side={'right'} offset={5} label={getTooltipLabel()}>
        <ViewToggle onClick={() => setShowConfirmation(true)}>
          <TrashIcon
            width={22}
            height={22}
            style={{ opacity: isLoading || error ? 0.5 : 1 }}
          />
        </ViewToggle>
      </TooltipWrapper>

      {showConfirmation && (
        <ConfirmationBackdrop onClick={handleCancel}>
          <ConfirmationContainer onClick={(e) => e.stopPropagation()}>
            <ConfirmationTitle>Delete VM</ConfirmationTitle>
            <ConfirmationMessage>
              Are you sure you want to delete this VM? This action cannot be
              undone.
            </ConfirmationMessage>
            <ButtonContainer>
              <CancelButton onClick={handleCancel}>Cancel</CancelButton>
              <ConfirmButton onClick={handleConfirm}>Delete</ConfirmButton>
            </ButtonContainer>
          </ConfirmationContainer>
        </ConfirmationBackdrop>
      )}
    </>
  )
}
